/**
 * useSettingsDialogUrl — keeps the address bar in step with an open Account or
 * Location settings dialog, whoever opened it.
 *
 *  - Opening PUSHES a history entry naming the dialog and its tab
 *    (utils/accountSettingsLink, utils/locationSettingsLink). A dialog opened
 *    FROM such an address (a notification link, a reload) adopts it instead.
 *  - Switching tab or sub-tab REPLACES that entry. Back is "leave the dialog",
 *    the way it is for every other dialog on the web; walking back through
 *    every tab visited first would make Back a chore, and the tabs keep their
 *    edits while the dialog is open anyway.
 *  - Back (or anything else that moves the address off the dialog) asks the
 *    dialog to close through `onHistoryClose` — the same path as Cancel, so
 *    unsaved changes are asked about first. When the dialog stays open
 *    ("Keep editing"), it calls `restoreUrl` to put its entry back.
 *  - Closing it in the page (✕, Cancel, Save) first rewrites the entry it
 *    pushed to the page's own address (no settings target), then steps back
 *    off it. history.back() alone would leave the settings address in the
 *    forward stack, and Forward would reopen a dialog the user closed; a
 *    rewritten entry is just the page again. Back then leaves the page as it
 *    would have before the dialog opened — no dead press on a duplicate. An
 *    entry it adopted (a link opened in a new tab) is rewritten without the
 *    settings target and stays where it is.
 *    Back from an OPEN dialog is different: the user chose history, so the
 *    entry stays forward-reachable and Forward reopens it, as the browser
 *    promises.
 *
 * The close-time work waits a tick: under React StrictMode an effect is torn
 * down and set up again at once, and the remount must find the entry still
 * there rather than racing an asynchronous history.back().
 */
import { useCallback, useEffect, useRef } from 'react';
import { stripAccountSettingsLink, withAccountSettingsLink, type AccountSettingsLink } from '../utils/accountSettingsLink';
import { stripLocationSettingsLink, withLocationSettingsLink } from '../utils/locationSettingsLink';
import {
  describesSettingsDialog,
  isSettingsDialogOpen,
  leaveSettingsThen,
  markSettingsDialogGivingBack,
  registerSettingsDialog,
  settingsDialogKey,
  unregisterSettingsDialog,
  type SettingsKind,
} from '../utils/settingsDialogs';
import { holdSettingsHistory, resetSettingsHistoryIdle, stepBackOffSettingsEntry, whenSettingsHistoryIdle } from '../utils/settingsHistory';

export type SettingsDialogUrlTarget =
  | {
      kind: 'account';
      accountId: string;
      tab: string;
      subTab?: string;
      section?: string;
      partnerConnectionId?: string;
      partnerTab?: AccountSettingsLink['partnerTab'];
    }
  | { kind: 'location'; locationId: string; accountId?: string; tab: string; subTab?: string };

/** history.state key: the dialog that pushed the current entry. */
export const SETTINGS_HISTORY_MARK = 'tcSettings';

const pendingCloses = new Map<string, { timer: ReturnType<typeof setTimeout>; done: () => void }>();

const idOf = (t: SettingsDialogUrlTarget): string => (t.kind === 'account' ? t.accountId : t.locationId);

function searchFor(search: string, t: SettingsDialogUrlTarget): string {
  if (t.kind === 'account') {
    return withAccountSettingsLink(search, {
      accountId: t.accountId,
      tab: t.tab,
      subTab: t.subTab,
      section: t.section,
      partnerConnectionId: t.partnerConnectionId,
      partnerTab: t.partnerTab,
    });
  }
  return withLocationSettingsLink(search, { locationId: t.locationId, accountId: t.accountId, tab: t.tab, subTab: t.subTab });
}

const urlWith = (search: string): string => `${window.location.pathname}${search}${window.location.hash}`;

function stateWith(mark: string | undefined): Record<string, unknown> {
  const state = { ...((window.history.state as Record<string, unknown> | null) ?? {}) };
  if (mark) state[SETTINGS_HISTORY_MARK] = mark;
  else delete state[SETTINGS_HISTORY_MARK];
  return state;
}

// When a closing page's history work is done (and the step back off its
// entry has landed): see utils/settingsHistory.
export { whenSettingsHistoryIdle, resetSettingsHistoryIdle };

/**
 * Leave every open settings page (each asks about unsaved edits; Keep editing
 * cancels), wait for its history step to land, then go. For anything outside
 * the settings page that moves to another view: a view pushed while settings
 * is still up stays hidden under it, and one pushed before its Back lands is
 * undone by that Back.
 */
export function leaveSettingsAndGo(go: () => void): void {
  leaveSettingsThen(() => whenSettingsHistoryIdle(go));
}

/** The dialog closed in the page: take its target out of the address. */
function releaseUrl(kind: SettingsKind, id: string, key: string): void {
  const search = window.location.search;
  // Back already took the address elsewhere (or the page navigated away).
  if (!describesSettingsDialog(search, kind, id)) return;
  const pushedHere = (window.history.state as Record<string, unknown> | null)?.[SETTINGS_HISTORY_MARK] === key;
  const stripped = kind === 'account' ? stripAccountSettingsLink(search) : stripLocationSettingsLink(search);
  window.history.replaceState(stateWith(undefined), '', urlWith(stripped));
  // The rewritten entry is left in the forward stack; Forward lands on the
  // page, not on the closed dialog.
  if (pushedHere) stepBackOffSettingsEntry();
}

export function useSettingsDialogUrl(target: SettingsDialogUrlTarget | null, onHistoryClose: () => void): { restoreUrl: () => void } {
  const targetRef = useRef(target);
  targetRef.current = target;
  const onHistoryCloseRef = useRef(onHistoryClose);
  onHistoryCloseRef.current = onHistoryClose;

  const kind = target?.kind;
  const id = target ? idOf(target) : undefined;
  const key = kind && id ? settingsDialogKey(kind, id) : null;

  // Open / close.
  useEffect(() => {
    const t = targetRef.current;
    if (!key || !t) return;
    const k = t.kind;
    const entityId = idOf(t);
    const pending = pendingCloses.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      pending.done();
      pendingCloses.delete(key);
    }
    registerSettingsDialog(key);

    const search = window.location.search;
    const next = searchFor(search, t);
    if (describesSettingsDialog(search, k, entityId)) {
      // Opened from its own address: adopt it (normalizing a legacy tab id).
      if (next !== search) window.history.replaceState(window.history.state, '', urlWith(next));
    } else {
      window.history.pushState(stateWith(key), '', urlWith(next));
    }

    return () => {
      // Whatever moves next waits until the entry is given back (and any step
      // back off it has landed): see utils/settingsHistory.
      const releaseHistory = holdSettingsHistory();
      const givenBack = markSettingsDialogGivingBack(key);
      const done = () => {
        givenBack();
        releaseHistory();
      };
      unregisterSettingsDialog(key);
      const timer = setTimeout(() => {
        pendingCloses.delete(key);
        if (!isSettingsDialogOpen(k, entityId)) releaseUrl(k, entityId, key);
        done();
      }, 0);
      pendingCloses.set(key, { timer, done });
    };
  }, [key]);

  // Tab / sub-tab switches rewrite the dialog's own entry.
  const signature = target ? searchFor('', target) : '';
  useEffect(() => {
    const t = targetRef.current;
    if (!key || !t) return;
    const search = window.location.search;
    if (!describesSettingsDialog(search, t.kind, idOf(t))) return;
    const next = searchFor(search, t);
    if (next !== search) window.history.replaceState(window.history.state, '', urlWith(next));
  }, [key, signature]);

  // Back / Forward away from the dialog's address: close it the way Cancel does.
  useEffect(() => {
    if (!key) return;
    const onPopState = () => {
      const t = targetRef.current;
      if (!t) return;
      if (!describesSettingsDialog(window.location.search, t.kind, idOf(t))) onHistoryCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [key]);

  /** The dialog stays open after Back (the user kept editing): name it in the address again. */
  const restoreUrl = useCallback(() => {
    const t = targetRef.current;
    if (!t) return;
    const search = window.location.search;
    if (describesSettingsDialog(search, t.kind, idOf(t))) return;
    window.history.pushState(stateWith(settingsDialogKey(t.kind, idOf(t))), '', urlWith(searchFor(search, t)));
  }, []);

  return { restoreUrl };
}
